import { createLogger } from '@/utils/logger';

export interface StorageWriteOptions {
  /** When false, skip info log for this call. Errors are still logged. Default: true. */
  log?: boolean;
  /** Extra attributes merged into the log; do NOT put the value itself here. */
  context?: Record<string, unknown>;
}

export interface AppStorage {
  /** Write to localStorage. Emits info (unless `log: false`). Rethrows on failure. */
  set(key: string, value: string, options?: StorageWriteOptions): void;
  /** Remove one key. Emits info (unless `log: false`). Rethrows on failure. */
  remove(key: string, options?: StorageWriteOptions): void;
  /** Remove multiple keys; emits a single summary log with failedCount; does not rethrow on per-key failure. */
  removeMany(keys: string[], options?: StorageWriteOptions): void;
  /** Read from localStorage; no info log. Returns null on missing/error. */
  get(key: string): string | null;
}

export function createStorage(moduleName: string): AppStorage {
  const logger = createLogger(`Storage:${moduleName}`);

  function isAvailable(): boolean {
    try {
      return typeof localStorage !== 'undefined' && localStorage != null;
    } catch {
      return false;
    }
  }

  const set: AppStorage['set'] = (key, value, options) => {
    if (!isAvailable()) {
      logger.warn('localStorage 不可用，跳过写入', {
        key,
        valueLength: value.length,
        ...options?.context,
      });
      return;
    }
    try {
      localStorage.setItem(key, value);
      if (options?.log !== false) {
        logger.info('写入 localStorage', {
          key,
          valueLength: value.length,
          ...options?.context,
        });
      }
    } catch (error) {
      logger.error('写入 localStorage 失败', error, {
        key,
        valueLength: value.length,
        ...options?.context,
      });
      throw error;
    }
  };

  const remove: AppStorage['remove'] = (key, options) => {
    if (!isAvailable()) {
      logger.warn('localStorage 不可用，跳过清除', { key, ...options?.context });
      return;
    }
    try {
      localStorage.removeItem(key);
      if (options?.log !== false) {
        logger.info('清除 localStorage', { key, ...options?.context });
      }
    } catch (error) {
      logger.error('清除 localStorage 失败', error, { key, ...options?.context });
      throw error;
    }
  };

  const removeMany: AppStorage['removeMany'] = (keys, options) => {
    if (!isAvailable()) {
      logger.warn('localStorage 不可用，跳过批量清除', {
        keys: keys.join(','),
        ...options?.context,
      });
      return;
    }
    const failed: string[] = [];
    for (const key of keys) {
      try {
        localStorage.removeItem(key);
      } catch {
        failed.push(key);
      }
    }
    if (options?.log !== false) {
      logger.info('批量清除 localStorage', {
        keys: keys.join(','),
        count: keys.length,
        failedCount: failed.length,
        ...options?.context,
      });
    }
    if (failed.length > 0) {
      logger.warn('批量清除 localStorage 部分失败', {
        failedKeys: failed.join(','),
        ...options?.context,
      });
    }
  };

  const get: AppStorage['get'] = (key) => {
    if (!isAvailable()) return null;
    try {
      return localStorage.getItem(key);
    } catch (error) {
      logger.error('读取 localStorage 失败', error, { key });
      return null;
    }
  };

  return { set, remove, removeMany, get };
}
